import React from 'react';
import './ThemeSelector.css';
import { useTheme } from '../../../contexts/ThemeContext';
import { THEME_CONFIGS, THEME_LABELS, LIGHT_THEMES, DARK_THEMES } from '../../../config/themes';
import { ThemeName } from '../../../types/theme';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const ThemeSelector: React.FC = () => {
  const { themeSelection, setLightTheme, setDarkTheme, currentTheme } = useTheme();

  const handleLightThemeSelect = (theme: ThemeName) => {
    setLightTheme(theme);
  };

  const handleDarkThemeSelect = (theme: ThemeName) => {
    setDarkTheme(theme);
  };

  return (
    <div className="theme-selector-container">
      {/* Light Themes */}
      <div className="theme-category">
        <h3>☀️ Light Themes</h3>
        <div className="theme-grid">
          {LIGHT_THEMES.map((themeName) => {
            const theme = THEME_CONFIGS[themeName];
            const isSelected = themeSelection.light === themeName;
            const isCurrent = currentTheme === themeName;

            return (
              <div
                key={themeName}
                className={`theme-card ${isSelected ? 'selected' : ''} ${
                  isCurrent ? 'current' : ''
                }`}
                onClick={() => handleLightThemeSelect(themeName)}
              >
                <div className="theme-preview">
                  <div
                    className="preview-color"
                    style={{
                      background: `linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.surface} 50%, ${theme.colors.background} 100%)`,
                    }}
                  />
                </div>
                <div className="theme-info">
                  <h4>{THEME_LABELS[themeName]}</h4>
                  {isSelected && (
                    <div className="badge selected-badge">
                      <FontAwesomeIcon icon='check'/> Selected
                    </div>
                  )}
                  {isCurrent && (
                    <div className="badge current-badge">Active</div>
                  )}
                </div>
                <div className="color-swatches">
                  <div
                    className="swatch"
                    style={{ backgroundColor: theme.colors.primary }}
                    title="Primary"
                  />
                  <div
                    className="swatch"
                    style={{ backgroundColor: theme.colors.text }}
                    title="Text"
                  />
                  <div
                    className="swatch"
                    style={{
                      backgroundColor: theme.colors.background,
                      border: '1px solid #ccc',
                    }}
                    title="Background"
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Dark Themes */}
      <div className="theme-category">
        <h3>🌙 Dark Themes</h3>
        <div className="theme-grid">
          {DARK_THEMES.map((themeName) => {
            const theme = THEME_CONFIGS[themeName];
            const isSelected = themeSelection.dark === themeName;
            const isCurrent = currentTheme === themeName;

            return (
              <div
                key={themeName}
                className={`theme-card ${isSelected ? 'selected' : ''} ${
                  isCurrent ? 'current' : ''
                }`}
                onClick={() => handleDarkThemeSelect(themeName)}
              >
                <div className="theme-preview">
                  <div
                    className="preview-color"
                    style={{
                      background: `linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.surface} 50%, ${theme.colors.background} 100%)`,
                    }}
                  />
                </div>
                <div className="theme-info">
                  <h4>{THEME_LABELS[themeName]}</h4>
                  {isSelected && (
                    <div className="badge selected-badge">
                      <FontAwesomeIcon icon='check'/> Selected
                    </div>
                  )}
                  {isCurrent && (
                    <div className="badge current-badge">Active</div>
                  )}
                </div>
                <div className="color-swatches">
                  <div
                    className="swatch"
                    style={{ backgroundColor: theme.colors.primary }}
                    title="Primary"
                  />
                  <div
                    className="swatch"
                    style={{ backgroundColor: theme.colors.text }}
                    title="Text"
                  />
                  <div
                    className="swatch"
                    style={{ backgroundColor: theme.colors.background }}
                    title="Background"
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="theme-selector-footer">
        <p className="info-text">
          💡 Tip: You can switch between themes anytime in the header, or customize them here for light and dark modes.
        </p>
      </div>
    </div>
  );
};

export default ThemeSelector;
